import React, { useState } from 'react';
import { Box, Typography, Button, Container, Avatar, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import logoSymbol from '../assets/icons/logo-symbol.svg';
import judgeIcon from '../assets/icons/iconsax-judge.svg';
import documentIcon from '../assets/icons/iconsax-document-text-2.svg';
import shieldSearchIcon from '../assets/icons/iconsax-shield-search.svg';
import likeIcon from '../assets/icons/like.svg';
import Footer from '../components/Footer';
import AuthModal from '../components/auth/AuthModal';
import { useAuth } from '../contexts/AuthContext';
import { 
  AutoFixHigh, 
  Speed, 
  Security, 
  CheckCircle, 
  Star,
  TrendingUp,
  People,
  BusinessCenter
} from '@mui/icons-material';
import { PAY_PER_USE_PRICING, SUBSCRIPTION_PLANS, CREDIT_VALUE_CZK, EXTRA_CREDIT_PRICE_CZK } from '../config/pricing';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('login');
  const { isAuthenticated } = useAuth();
  // Removed demo modal state along with CTA button

  const stats = [
    { value: '70%', label: '70% úspora času = více příležitostí pro růst', icon: <Speed /> },
    { value: '98%', label: 'Přesné odpovědi pro český trh', icon: <People /> },
    { value: '24/7', label: 'Dostupné kdykoliv potřebujete', icon: <Security /> }
  ];

  const features = [
    {
      icon: <AutoFixHigh sx={{ fontSize: 32 }} />,
      title: 'AI-Powered Analýza',
      description: 'Pokročilé algoritmy analyzují vaše právní dokumenty a poskytují přesné odpovědi v reálném čase.',
      benefit: 'Úspora až 80% času'
    },
    {
      icon: <Speed sx={{ fontSize: 32 }} />,
      title: 'Okamžité Odpovědi',
      description: 'Získejte odpovědi na právní otázky během několika minut, ne hodin nebo dnů.',
      benefit: 'Rychlá odpověď'
    },
    {
      icon: <Security sx={{ fontSize: 32 }} />,
      title: 'Ochrana dat',
      description: 'Bezpečná ochrana dat a soukromí podle GDPR standardů.',
      benefit: 'Bezpečné šifrování'
    },
    {
      icon: <CheckCircle sx={{ fontSize: 32 }} />,
      title: 'Ověřené Informace',
      description: 'Všechny odpovědi jsou ověřeny proti aktuální legislativě a judikatuře.',
      benefit: 'Aktualizace denně'
    }
  ];

  const testimonials = [
    {
      name: 'Ing. Marie Nováková',
      role: 'CEO, TechCorp s.r.o.',
      content: 'Díky AI právníkovi jsme ušetřili tisíce korun na právních poradnách. Systém je neuvěřitelně přesný.',
      rating: 5,
      avatar: 'MN'
    },
    {
      name: 'JUDr. Pavel Svoboda',
      role: 'Advokátní kancelář',
      content: 'Používám jako první kontrolu před vlastní analýzou. Kvalita odpovědí mě stále překvapuje.',
      rating: 5,
      avatar: 'PS'
    },
    {
      name: 'Jana Procházková',
      role: 'Majitelka e-shopu',
      content: 'Konečně rozumím svým právním povinnostem. Systém vysvětluje vše jasně a srozumitelně.',
      rating: 5,
      avatar: 'JP'
    }
  ];

  const formatCurrency = (value: number) => value.toLocaleString('cs-CZ');
  const creditValue = CREDIT_VALUE_CZK.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const extraCreditPrice = EXTRA_CREDIT_PRICE_CZK.toLocaleString('cs-CZ');

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh',
      backgroundColor: '#F2F2F2',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Container maxWidth={false} sx={{ 
        maxWidth: '1920px',
        px: { xs: 2, sm: 3, md: 4, lg: '277px' }
      }}>
        {/* Hero Section */}
        <Box sx={{
          position: 'relative',
          mt: { xs: '80px', sm: '96px', md: '132px' },
          mb: { xs: 6, sm: 8, md: 12 }
        }}>
          {/* Enhanced Gradient Background - optimized for mobile */}
          <Box sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '200%', sm: '180%', md: '160%', lg: '1600px' },
            height: { xs: '300px', sm: '400px', md: '500px' },
            borderRadius: '50%',
            background: 'radial-gradient(circle at 50% 50%, rgba(100, 100, 255, 0.1) 0%, rgba(255, 100, 100, 0.05) 30%, rgba(128, 128, 128, 0.3) 60%, rgba(237, 237, 237, 0) 100%)',
            filter: { xs: 'blur(60px)', sm: 'blur(80px)', md: 'blur(120px)' },
            zIndex: 0,
            willChange: 'transform'
          }} />
          
          <Box sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 2.5, sm: 3.5, md: 5 },
            py: { xs: 4, sm: 6, md: 8 },
            zIndex: 1
          }}>

            <Box 
              component="img" 
              src={logoSymbol} 
              alt="Logo" 
              sx={{ 
                width: { xs: '80px', sm: '100px', md: '120px' },
                height: { xs: '80px', sm: '100px', md: '120px' },
                filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.1))'
              }} 
            />
            
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 800,
              fontSize: { xs: '32px', sm: '42px', md: '52px', lg: '64px' },
              lineHeight: { xs: 1.1, md: 1.1 },
              textAlign: 'center',
              color: '#1F1F1F',
              maxWidth: { xs: '100%', sm: '700px', md: '900px', lg: '1100px' },
              px: { xs: 2, sm: 3 }
            }}>
              <Box component="span" sx={{ color: '#e72f0d', display: 'block' }}>
                Právo bez složitostí.
              </Box>
              <Box component="span" sx={{ display: 'block' }}>
                Jasně a srozumitelně.
              </Box>
            </Typography>

            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: { xs: '16px', sm: '18px', md: '20px' },
              lineHeight: 1.5,
              textAlign: 'center',
              color: '#666666',
              maxWidth: '600px',
              px: { xs: 2, sm: 3 }
            }}>
              „K soudu už jedině s právnimbotem.“ Eva Strejcová, Apertia
            </Typography>
            
            <Box sx={{
              display: 'flex',
              gap: { xs: 2, sm: 3 },
              flexDirection: { xs: 'column', sm: 'row' },
              width: { xs: '100%', sm: 'auto' },
              maxWidth: { xs: '400px', sm: 'none' },
              justifyContent: 'center',
              mt: { xs: 1.5, sm: 2 }
            }}>
              <Button
                onClick={() => {
                  navigate('/dashboard', { state: { requireRegistration: true, authMode: 'login', intendedPath: '/' } });
                }}
                size="large"
                sx={{
                  minHeight: '48px',
                  padding: { xs: '14px 28px', sm: '16px 32px' },
                  bgcolor: '#1F1F1F !important',
                  backgroundColor: '#1F1F1F !important',
                  background: '#1F1F1F !important',
                  color: '#FFFFFF !important',
                  fontSize: { xs: '15px', sm: '16px' },
                  fontWeight: 600,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  border: 'none !important',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: '#e72f0d !important',
                    backgroundColor: '#e72f0d !important',
                    background: '#e72f0d !important',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 40px rgba(231, 47, 13, 0.25)'
                  }
                }}
              >
                Otevřít aplikaci
              </Button>
              <Button
                onClick={() => {
                  navigate('/pribeh');
                }}
                size="large"
                variant="outlined"
                sx={{
                  minHeight: '48px',
                  padding: { xs: '14px 28px', sm: '16px 32px' },
                  borderRadius: '999px',
                  borderColor: '#1F1F1F',
                  color: '#1F1F1F',
                  fontSize: { xs: '15px', sm: '16px' },
                  fontWeight: 600,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#e72f0d',
                    color: '#e72f0d',
                    backgroundColor: 'rgba(231,47,13,0.06)',
                    transform: { xs: 'none', sm: 'translateY(-2px)' }
                  }
                }}
              >
                Příběh vzniku právního bota
              </Button>
            </Box>

            {/* Simple Guide for New Users */}
            <Box sx={{
              mt: { xs: 3, sm: 4 },
              p: { xs: 2.5, sm: 3 },
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(0,0,0,0.06)',
              maxWidth: 900,
              mx: 'auto'
            }}>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: { xs: '20px', sm: '22px' },
                color: '#1F1F1F',
                textAlign: 'center',
                mb: 1.5
              }}>
                Jak začít.
              </Typography>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '15px' },
                color: '#666',
                textAlign: 'center',
                mb: 2.5
              }}>
                Bez registrace to nepůjde – zabere to 30 sekund a pak máte všechno na jednom místě v Dashboardu.
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '15px', sm: '16px' } }}>
                      1) Klikněte na „Otevřít aplikaci“
                    </Typography>
                    <Typography sx={{ color: '#555', fontSize: { xs: '14px', sm: '15px' } }}>
                      Otevře se okno s přihlášením. Pokud účet ještě nemáte, přepněte na „Registrace“ a vyplňte 3 políčka.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '15px', sm: '16px' } }}>
                      2) Dostanete se do Dashboardu
                    </Typography>
                    <Typography sx={{ color: '#555', fontSize: { xs: '14px', sm: '15px' } }}>
                      Tady najdete všechny funkce: Právní konzultace, Generování dokumentů, Analýza smluv a Právní výzkum.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '15px', sm: '16px' } }}>
                      3) Vyberte, co potřebujete
                    </Typography>
                    <Typography sx={{ color: '#555', fontSize: { xs: '14px', sm: '15px' } }}>
                      Konzultace: napište dotaz a odpovídejte na doplňující otázky. Dokumenty: vyplňte formulář a stáhněte PDF. 
                      Analýza: nahrajte smlouvu a vyberte, zda jste první nebo druhá strana. Výzkum: zadejte právní dotaz.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '15px', sm: '16px' } }}>
                      4) Vše máte uložené
                    </Typography>
                    <Typography sx={{ color: '#555', fontSize: { xs: '14px', sm: '15px' } }}>
                      Vaše konzultace, dokumenty a analýzy najdete kdykoli v Dashboardu. Můžete se vrátit a pokračovat.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

          </Box>
        </Box>

        {/* Stats Section */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' },
          gap: { xs: 2, sm: 3 },
          mb: { xs: 8, sm: 10, md: 12 },
          width: '100%',
          maxWidth: '900px',
          mx: 'auto',
          justifyItems: 'center'
        }}>
          {stats.map((stat, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                padding: { xs: '24px 16px', sm: '32px 20px' },
                backgroundColor: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                transition: 'all 0.5s ease-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                }
              }}
            >
              <Box sx={{ color: '#1F1F1F', mb: 1 }}>
                {stat.icon}
              </Box>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: { xs: '24px', sm: '28px' },
                color: '#1F1F1F'
              }}>
                {stat.value}
              </Typography>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: { xs: '12px', sm: '14px' },
                color: '#666666',
                textAlign: 'center'
              }}>
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Features Section */}
        <Box sx={{
          position: 'relative',
          mb: { xs: 8, sm: 10, md: 12 }
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 4, sm: 5, md: 6 },
            mb: { xs: 4, sm: 6 }
          }}>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: { xs: '28px', sm: '36px', md: '42px' },
              lineHeight: 1.2,
              textAlign: 'center',
              color: '#1F1F1F',
              maxWidth: '800px'
            }}>
              Proč si vybrat našeho AI právníka?
            </Typography>
            
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: { xs: 3, sm: 4 },
              width: '100%'
            }}>
              {features.map((feature, index) => (
                <Box
                  key={index}
                  onMouseEnter={() => setHoveredFeature(index)}
                  onMouseLeave={() => setHoveredFeature(null)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    padding: { xs: '32px 24px', sm: '40px 32px' },
                    backgroundColor: hoveredFeature === index 
                      ? 'rgba(255,255,255,1)' 
                      : 'rgba(255,255,255,0.9)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '24px',
                    boxShadow: hoveredFeature === index 
                      ? '0 12px 40px rgba(0,0,0,0.15)' 
                      : '0 4px 20px rgba(0,0,0,0.08)',
                    transition: 'all 0.5s ease-out',
                    cursor: 'pointer',
                    transform: hoveredFeature === index ? 'translateY(-8px)' : 'translateY(0)',
                    border: hoveredFeature === index ? '2px solid rgba(231,47,13,0.2)' : '2px solid transparent'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      color: hoveredFeature === index ? '#e72f0d' : '#1F1F1F',
                      transition: 'color 0.4s ease-out'
                    }}>
                      {feature.icon}
                    </Box>
                    <Chip 
                      label={feature.benefit}
                      size="small"
                      sx={{ 
                        backgroundColor: hoveredFeature === index ? '#e72f0d' : '#E8F5E8',
                        color: hoveredFeature === index ? 'white' : '#2E7D32',
                        fontWeight: 600,
                        fontSize: '11px'
                      }}
                    />
                  </Box>
                  
                  <Box>
                    <Typography sx={{
                      fontFamily: 'Inter',
                      fontWeight: 600,
                      fontSize: { xs: '20px', sm: '22px' },
                      color: '#1F1F1F',
                      mb: 1
                    }}>
                      {feature.title}
                    </Typography>
                    <Typography sx={{
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: { xs: '14px', sm: '15px' },
                      lineHeight: 1.6,
                      color: '#666666'
                    }}>
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Services Grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(3, 1fr)' 
          },
          gap: { xs: 3, sm: 4 },
          mb: { xs: 8, sm: 10, md: 12 }
        }}>
          {/* Právní konzultace */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: { xs: 3, sm: 4 },
            padding: { xs: '32px 24px', sm: '40px 32px' },
            backgroundColor: '#FFFFFF',
            borderRadius: '24px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.5s ease-out',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)'
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box 
                component="img" 
                src={judgeIcon} 
                alt="Judge" 
                sx={{ 
                  width: { xs: '40px', sm: '48px' },
                  height: { xs: '40px', sm: '48px' }
                }} 
              />
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: { xs: '22px', sm: '24px' },
                lineHeight: 1.3,
                color: '#1F1F1F'
              }}>
                Právní konzultace
              </Typography>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '15px' },
                lineHeight: 1.5,
                color: '#666666'
              }}>
                Získejte rychlé a přesné odpovědi na právní otázky s pomocí AI analýzy českého práva. Úspora až 80% času oproti klasickým konzultacím.
              </Typography>
              <Chip 
                label="Rychlé odpovědi" 
                size="small" 
                sx={{ 
                  alignSelf: 'flex-start',
                  backgroundColor: '#E8F5E8',
                  color: '#2E7D32',
                  fontWeight: 600
                }}
              />
            </Box>
              <Button
                onClick={() => {
                  const intended = '/dashboard?open=consultation';
                  try { sessionStorage.setItem('intendedPath', intended); } catch {}
                  if (isAuthenticated) {
                    navigate(intended);
                  } else {
                    navigate(`/dashboard?requireRegistration=1&authMode=login&intendedPath=${encodeURIComponent(intended)}`);
                  }
                }}
                variant="outlined"
              sx={{
                width: '100%',
                padding: { xs: '14px 28px', sm: '16px 32px' },
                color: '#1F1F1F',
                borderColor: '#1F1F1F',
                fontSize: { xs: '14px', sm: '16px' },
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(31, 31, 31, 0.08)',
                  borderColor: '#1F1F1F'
                }
              }}
            >
              Začít konzultaci →
            </Button>
          </Box>

          {/* Generování dokumentů */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: { xs: 3, sm: 4 },
            padding: { xs: '32px 24px', sm: '40px 32px' },
            backgroundColor: '#FFFFFF',
            borderRadius: '24px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.5s ease-out',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)'
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box 
                component="img" 
                src={documentIcon} 
                alt="Document" 
                sx={{ 
                  width: { xs: '40px', sm: '48px' },
                  height: { xs: '40px', sm: '48px' }
                }} 
              />
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: { xs: '22px', sm: '24px' },
                lineHeight: 1.3,
                color: '#1F1F1F'
              }}>
                Generování dokumentů
              </Typography>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '15px' },
                lineHeight: 1.5,
                color: '#666666'
              }}>
                Vytvářejte profesionální právní dokumenty podle českého práva během několika minut. Smlouvy, žádosti, stížnosti a více.
              </Typography>
              <Chip 
                label="5+ šablon" 
                size="small" 
                sx={{ 
                  alignSelf: 'flex-start',
                  backgroundColor: '#FFF3E0',
                  color: '#F57C00',
                  fontWeight: 600
                }}
              />
            </Box>
            <Button
              onClick={() => {
                const intended = '/dashboard?open=documents';
                try { sessionStorage.setItem('intendedPath', intended); } catch {}
                if (isAuthenticated) {
                  navigate(intended);
                } else {
                  navigate(`/dashboard?requireRegistration=1&authMode=login&intendedPath=${encodeURIComponent(intended)}`);
                }
              }}
              variant="outlined"
              sx={{
                width: '100%',
                padding: { xs: '14px 28px', sm: '16px 32px' },
                color: '#1F1F1F',
                borderColor: '#1F1F1F',
                fontSize: { xs: '14px', sm: '16px' },
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(31, 31, 31, 0.08)',
                  borderColor: '#1F1F1F'
                }
              }}
            >
              Vytvořit dokument →
            </Button>
          </Box>

          {/* Právní výzkum */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: { xs: 3, sm: 4 },
            padding: { xs: '32px 24px', sm: '40px 32px' },
            backgroundColor: '#FFFFFF',
            borderRadius: '24px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
            gridColumn: { xs: 'span 1', sm: 'span 2', md: 'span 1' },
            transition: 'all 0.5s ease-out',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)'
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box 
                component="img" 
                src={shieldSearchIcon} 
                alt="Shield Search" 
                sx={{ 
                  width: { xs: '40px', sm: '48px' },
                  height: { xs: '40px', sm: '48px' }
                }} 
              />
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: { xs: '22px', sm: '24px' },
                lineHeight: 1.3,
                color: '#1F1F1F'
              }}>
                Právní výzkum
              </Typography>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '15px' },
                lineHeight: 1.5,
                color: '#666666'
              }}>
                Prohledávejte zákony, judikaturu a právní předpisy s pokročilou AI asistencí. Rychlé vyhledávání v celé české legislativě.
              </Typography>
              <Chip 
                label="Právní databáze" 
                size="small" 
                sx={{ 
                  alignSelf: 'flex-start',
                  backgroundColor: '#E3F2FD',
                  color: '#1976D2',
                  fontWeight: 600
                }}
              />
            </Box>
            <Button
              onClick={() => {
                const intended = '/dashboard?open=research';
                try { sessionStorage.setItem('intendedPath', intended); } catch {}
                if (isAuthenticated) {
                  navigate(intended);
                } else {
                  navigate(`/dashboard?requireRegistration=1&authMode=login&intendedPath=${encodeURIComponent(intended)}`);
                }
              }}
              variant="outlined"
              sx={{
                width: '100%',
                padding: { xs: '14px 28px', sm: '16px 32px' },
                color: '#1F1F1F',
                borderColor: '#1F1F1F',
                fontSize: { xs: '14px', sm: '16px' },
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(31, 31, 31, 0.08)',
                  borderColor: '#1F1F1F'
                }
              }}
            >
              Začít výzkum →
            </Button>
          </Box>
        </Box>


        {/* Pricing */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 4, sm: 6 },
          mb: { xs: 8, sm: 10, md: 12 }
        }}>
          <Box sx={{ textAlign: 'center', maxWidth: '760px' }}>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: { xs: '28px', sm: '36px', md: '42px' },
              lineHeight: 1.2,
              color: '#1F1F1F',
              mb: 2
            }}>
              Transparentní ceny a kreditní systém
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: { xs: '16px', sm: '18px' },
              color: '#666666'
            }}>
              1 kredit = {creditValue} Kč. Platby probíhají formou kreditů – například 50 Kč odpovídá 200 kreditům. Kredity strháváme až po dokončení služby.
            </Typography>
          </Box>

          <Box sx={{
            width: '100%',
            maxWidth: '1100px',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: { xs: 3, sm: 4 }
          }}>
            {PAY_PER_USE_PRICING.map((item) => (
              <Box
                key={item.key}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  padding: { xs: '28px 24px', sm: '36px 28px' },
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  borderRadius: '24px',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
                  border: '1px solid rgba(231,47,13,0.12)',
                  minHeight: '100%'
                }}
              >
                <Typography sx={{
                  fontFamily: 'Inter',
                  fontWeight: 700,
                  fontSize: { xs: '20px', sm: '22px' },
                  color: '#1F1F1F'
                }}>
                  {item.name}
                </Typography>
                <Typography sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: { xs: '14px', sm: '15px' },
                  color: '#424242',
                  lineHeight: 1.6
                }}>
                  {item.description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={`${formatCurrency(item.priceCZK)} Kč`}
                    sx={{
                      backgroundColor: '#1F1F1F',
                      color: '#FFFFFF',
                      fontWeight: 600
                    }}
                  />
                  <Chip
                    label={`${formatCurrency(item.credits)} kreditů`}
                    sx={{
                      backgroundColor: 'rgba(231,47,13,0.12)',
                      color: '#e72f0d',
                      fontWeight: 600
                    }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: '#616161', mt: 'auto' }}>
                  Cena se odečte po dokončení úkonu ({formatCurrency(item.credits)} kreditů = {formatCurrency(item.priceCZK)} Kč včetně DPH).
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ textAlign: 'center', maxWidth: '760px', mt: { xs: 4, sm: 6 } }}>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: { xs: '24px', sm: '30px' },
              color: '#1F1F1F',
              mb: 1
            }}>
              Kreditní tarify s měsíčním předplatným
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: { xs: '15px', sm: '16px' },
              color: '#666666'
            }}>
              Čím vyšší tarif, tím výhodnější cena za kredit. Nevyužité kredity se převádějí do dalšího měsíce (maximálně jeden měsíc).
            </Typography>
          </Box>

          <Box sx={{
            width: '100%',
            maxWidth: '1100px',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: { xs: 3, sm: 4 },
            justifyItems: 'center'
          }}>
            {SUBSCRIPTION_PLANS.map((plan, index) => {
              const isPopular = plan.key === 'standard';
              const isCustom = plan.key === 'custom';
              return (
                <Box
                  key={plan.key}
                  sx={{
                    gridColumn: isCustom ? { xs: '1', md: '2' } : undefined,
                    gridRow: isCustom ? { md: '2' } : undefined,
                    width: '100%',
                    maxWidth: isCustom ? { md: '360px' } : undefined,
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    padding: { xs: '32px 24px', sm: '40px 32px' },
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    borderRadius: '24px',
                    boxShadow: isPopular ? '0 16px 40px rgba(231,47,13,0.2)' : '0 10px 28px rgba(0,0,0,0.08)',
                    border: isPopular ? '2px solid rgba(231,47,13,0.35)' : '1px solid rgba(0,0,0,0.06)',
                    minHeight: '100%'
                  }}
                >
                  {isPopular && (
                    <Chip
                      label="Nejoblíbenější"
                      sx={{
                        position: 'absolute',
                        top: -14,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#e72f0d',
                        color: '#FFFFFF',
                        fontWeight: 600,
                        boxShadow: '0 8px 18px rgba(231,47,13,0.3)'
                      }}
                    />
                  )}

                  <Typography sx={{
                    fontFamily: 'Inter',
                    fontWeight: 700,
                    fontSize: { xs: '20px', sm: '24px' },
                    color: '#1F1F1F',
                    textTransform: 'uppercase'
                  }}>
                    {plan.name}
                  </Typography>
                  {plan.tagline && (
                    <Typography
                      sx={{
                        fontFamily: 'Inter',
                        fontWeight: 600,
                        fontSize: { xs: '13px', sm: '14px' },
                        color: '#e72f0d',
                        mt: 0.5
                      }}
                    >
                      {plan.tagline}
                    </Typography>
                  )}
                  <Typography sx={{
                    fontFamily: 'Inter',
                    fontWeight: 800,
                    fontSize: { xs: '30px', sm: '36px' },
                    color: '#1F1F1F'
                  }}>
                    {plan.priceCZK !== undefined ? (
                      <>
                        {formatCurrency(plan.priceCZK)} Kč
                        <span style={{ fontSize: '16px', fontWeight: 500 }}>/ měsíc</span>
                      </>
                    ) : (
                      plan.priceLabel || ''
                    )}
                  </Typography>
                  <Typography sx={{
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    fontSize: { xs: '16px', sm: '18px' },
                    color: '#e72f0d'
                  }}>
                    {plan.credits !== undefined
                      ? `${formatCurrency(plan.credits)} kreditů / měsíc`
                      : plan.creditsLabel || ''}
                  </Typography>
                  {plan.userLabel && (
                    <Typography sx={{
                      fontFamily: 'Inter',
                      fontWeight: 500,
                      fontSize: { xs: '13px', sm: '14px' },
                      color: '#1F1F1F'
                    }}>
                      {plan.userLabel}
                    </Typography>
                  )}
                  <Typography sx={{
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: { xs: '14px', sm: '15px' },
                    color: '#424242',
                    lineHeight: 1.6,
                    textAlign: plan.key === 'custom' ? 'center' : 'left'
                  }}>
                    {plan.description}
                  </Typography>
                  {plan.usageExamples && plan.usageExamples.length > 0 && (
                    <>
                      <Typography
                        sx={{
                          fontFamily: 'Inter',
                          fontWeight: 600,
                          fontSize: { xs: '14px', sm: '15px' },
                          color: '#1F1F1F',
                          mt: 1
                        }}
                      >
                        Zhruba vystačí na:
                      </Typography>
                      <Box
                        component="ul"
                        sx={{
                          listStyle: 'disc',
                          pl: 3,
                          m: 0,
                          color: '#424242',
                          fontFamily: 'Inter',
                          fontWeight: 400,
                          fontSize: { xs: '14px', sm: '15px' },
                          lineHeight: 1.6,
                          '& > li': { mb: 0.5 }
                        }}
                      >
                        {plan.usageExamples.map(example => (
                          <Box component="li" key={example}>
                            {example}
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                  <Button
                    onClick={() => {
                      if (plan.key === 'custom') {
                        navigate('/kontakty');
                      } else {
                        navigate(`/dashboard?requireRegistration=1&authMode=login&intendedPath=${encodeURIComponent('/dashboard')}`);
                      }
                    }}
                    variant="contained"
                    sx={{
                      mt: 'auto',
                      padding: { xs: '12px 20px', sm: '14px 24px' },
                      backgroundColor: plan.key === 'custom' ? '#e72f0d !important' : '#1F1F1F !important',
                      borderRadius: '999px',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
                      '&:hover': {
                        backgroundColor: plan.key === 'custom' ? '#c41f00 !important' : '#e72f0d !important',
                        boxShadow: '0 10px 22px rgba(231,47,13,0.35)'
                      }
                    }}
                  >
                    {plan.key === 'custom' ? 'Domluvit konzultaci' : 'Vybrat tarif'}
                  </Button>
                </Box>
              );
            })}
          </Box>

          <Typography sx={{
            fontFamily: 'Inter',
            fontWeight: 500,
            fontSize: { xs: '14px', sm: '15px' },
            color: '#555555',
            textAlign: 'center',
            maxWidth: '780px'
          }}>
            Nevyužité kredity přenášíme do dalšího měsíce (maximálně jeden měsíc). Po vyčerpání limitu můžete dokoupit další kredity za {extraCreditPrice} Kč / kredit. Všechny uvedené ceny jsou včetně DPH.
          </Typography>
        </Box>

        {/* FAQ Section */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 4, sm: 6 },
          mb: { xs: 8, sm: 10, md: 12 }
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: { xs: '28px', sm: '36px', md: '42px' },
              lineHeight: 1.2,
              color: '#1F1F1F',
              mb: 2
            }}>
              Často kladené otázky
            </Typography>
          </Box>
          
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: { xs: 3, sm: 4 },
            width: '100%',
            maxWidth: '1000px'
          }}>
            {/* FAQ Item 1 */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: { xs: '24px', sm: '32px' },
              backgroundColor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: '20px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'all 0.5s ease-out',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
              }
            }}>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: { xs: '16px', sm: '18px' },
                color: '#1F1F1F',
                mb: 1
              }}>
                Je to bezpečné?
              </Typography>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '15px' },
                lineHeight: 1.6,
                color: '#666666'
              }}>
                Bankovní úroveň šifrování a GDPR compliance. Vaše data jsou v bezpečí.
              </Typography>
            </Box>

            {/* FAQ Item 2 */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: { xs: '24px', sm: '32px' },
              backgroundColor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: '20px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'all 0.5s ease-out',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
              }
            }}>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: { xs: '16px', sm: '18px' },
                color: '#1F1F1F',
                mb: 1
              }}>
                Je to stejně dobré jako advokát?
              </Typography>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '15px' },
                lineHeight: 1.6,
                color: '#666666'
              }}>
                98% přesnost ověřená českými právníky. Ideální pro rutinní právní úkony.
              </Typography>
            </Box>

            {/* FAQ Item 3 */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: { xs: '24px', sm: '32px' },
              backgroundColor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: '20px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'all 0.5s ease-out',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
              }
            }}>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: { xs: '16px', sm: '18px' },
                color: '#1F1F1F',
                mb: 1
              }}>
                Odkud berete data?
              </Typography>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '15px' },
                lineHeight: 1.6,
                color: '#666666'
              }}>
                Oficiální databáze zákonů ČR, judikatura NSS a NS, rozhodnutí ESLP a SDEU.
              </Typography>
            </Box>

            {/* FAQ Item 4 */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: { xs: '24px', sm: '32px' },
              backgroundColor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: '20px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'all 0.5s ease-out',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
              }
            }}>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: { xs: '16px', sm: '18px' },
                color: '#1F1F1F',
                mb: 1
              }}>
                Jak často aktualizujete data?
              </Typography>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '15px' },
                lineHeight: 1.6,
                color: '#666666'
              }}>
                Denní aktualizace legislativy a týdenní aktualizace judikatury pro maximální přesnost.
              </Typography>
            </Box>

            {/* FAQ Item 5 */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: { xs: '24px', sm: '32px' },
              backgroundColor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: '20px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'all 0.5s ease-out',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
              }
            }}>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: { xs: '16px', sm: '18px' },
                color: '#1F1F1F',
                mb: 1
              }}>
                Funguje to s našimi systémy?
              </Typography>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '15px' },
                lineHeight: 1.6,
                color: '#666666'
              }}>
                Export do PDF, DOCX, TXT. REST API pro snadnou integraci s vašimi systémy.
              </Typography>
            </Box>

            {/* FAQ Item 6 */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: { xs: '24px', sm: '32px' },
              backgroundColor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: '20px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'all 0.5s ease-out',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
              }
            }}>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: { xs: '16px', sm: '18px' },
                color: '#1F1F1F',
                mb: 1
              }}>
                Můžu si to vyzkoušet zdarma?
              </Typography>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '15px' },
                lineHeight: 1.6,
                color: '#666666'
              }}>
                Registrace je zdarma. Jednotlivé úkony hradíte kredity – například 200 kreditů (50 Kč) za konzultaci nebo 400 kreditů (100 Kč) za analýzu smlouvy. V měsíčních tarifech je cena za kredit ještě výhodnější.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Professional Warning */}
        <Box sx={{
          position: 'relative',
          mb: { xs: 6, sm: 8, md: 10 }
        }}>
          <Box sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            padding: { xs: '16px', sm: '20px' },
            backgroundColor: '#4C4C4C',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            zIndex: 1
          }}>
            <Box 
              component="img" 
              src={likeIcon} 
              alt="Like" 
              sx={{ 
                width: { xs: '20px', sm: '24px' },
                height: { xs: '20px', sm: '24px' }
              }} 
            />
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 500,
              fontSize: { xs: '12px', sm: '14px' },
              lineHeight: 1.5,
              textAlign: 'center',
              color: '#DCDCDC'
            }}>
              Profesionální upozornění: Systém poskytuje obecné právní informace s AI verifikací. Pro konkrétní právní záležitosti konzultujte kvalifikovaného advokáta.
            </Typography>
          </Box>
        </Box>

        {/* Final CTA */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 4, sm: 5 },
          padding: { xs: '48px 32px', sm: '64px 40px' },
          background: 'linear-gradient(135deg, #1F1F1F 0%, #333333 100%)',
          borderRadius: '24px',
          boxShadow: '0px 20px 60px rgba(0, 0, 0, 0.25)',
          mb: { xs: 6, sm: 8, md: 10 }
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: 'Inter',
                fontWeight: 800,
                fontSize: { xs: '32px', sm: '42px', md: '48px' },
                lineHeight: 1.1,
                color: '#FFFFFF',
                mb: 2
              }}
            >
              Připraveni začít už dnes?
            </Typography>
            {/* Lepší slogan */}
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 500,
              fontSize: { xs: '16px', sm: '18px' },
              lineHeight: 1.6,
              color: '#D0D0D0',
              maxWidth: '720px',
              mx: 'auto'
            }}>
              Od dotazu k právní odpovědi za minuty.
            </Typography>
          </Box>
          
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            gap: { xs: 2, sm: 3 },
            justifyContent: 'center',
            textAlign: 'center'
          }}>
            <Button
              onClick={() => { setAuthModalMode('register'); setAuthModalOpen(true); }}
              size="large"
              sx={{
                padding: { xs: '14px 28px', sm: '16px 32px' },
                backgroundColor: '#FFFFFF',
                color: '#1F1F1F',
                borderRadius: '10px',
                fontSize: { xs: '16px', sm: '18px' },
                fontWeight: 700,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                boxShadow: '0 8px 32px rgba(255,255,255,0.2)',
                '&:hover': {
                  backgroundColor: '#F0F0F0',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(255,255,255,0.3)'
                }
              }}
            >
              Zaregistruj se zde
            </Button>
          </Box>

          <Typography sx={{
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: { xs: '14px', sm: '16px' },
            lineHeight: 1.6,
            color: '#C7C7C7',
            maxWidth: '760px',
            textAlign: 'center'
          }}>
            Získáte osobní dashboard, ve kterém budete mít naprostý přehled nad konzultacemi, dokumenty i analýzami.
          </Typography>

          {/* Removed mini-claim row as requested */}
        </Box>
      </Container>

      {/* Footer */}
      <Footer />

      {/* Auth Modal */}
      <AuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode={authModalMode}
      />

      {/* Demo Request Modal removed with CTA */}
    </Box>
  );
};

export default HomePage;
